<template lang="pug">
#family-permitted-function-edit-tab-component
  .list-container.d-flex.flex-row.align-items-center.x-scroll.mt-4
    // left side list
    .family-list.d-flex.flex-column
      .list-title
        span 使用不可
      .list-body
        .list-body-title.d-flex.flex-row.align-items-center
          span.group-select-title 利用者グループ
          select.group-select-box(v-model="unuseFilteredResidentGroupId")
            option(v-for="option in unuseResidentGroupOptions" :value="option.value") {{ option.label }}
        .list-body-searchword.d-flex.flex-row.align-items-center
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="unuseResidentSearchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(:popoverId="'left-search-filter-help-' + type" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・利用者名
              div ・利用者名（ふりがな）
              div ・部屋名
              div ・家族ユーザー名
              div ・役割
        .list-body-line(v-for="family in _filteredUnuseFamilies" :key="family.id + '-' + family.familyId").d-flex.flex-row
          b-form-checkbox(v-model="family.isTarget")
            span.family-name {{ family.familyName }}（{{ family.room }}・{{ family.name }}・{{ family.familyRelation }}）

    // center buttons
    .move-buttons.d-flex.flex-column
      button.btn.move-right-btn(@click="changePermittedFunction(families, false)" type="button") 右に移す→
      button.btn.move-left-btn(@click="changePermittedFunction(families, true)" type="button") ←左に移す

    // right side list
    .family-list.d-flex.flex-column
      .list-title
        span 使用可
      .list-body
        .list-body-title.d-flex.flex-row.align-items-center
          span.group-select-title 利用者グループ
          select.group-select-box(v-model="useFilteredResidentGroupId")
            option(v-for="option in useResidentGroupOptions" :value="option.value") {{ option.label }}
        .list-body-searchword.d-flex.flex-row.align-items-center
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="useResidentSearchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(:popoverId="'right-search-filter-help-' + type" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・利用者名
              div ・利用者名（ふりがな）
              div ・部屋名
              div ・家族ユーザー名
              div ・役割
        .list-body-line(v-for="family in _filteredUseFamilies" :key="family.id + '-' + family.familyId").d-flex.flex-row
          b-form-checkbox(v-model="family.isTarget")
            span.family-name {{ family.familyName }}（{{ family.room }}・{{ family.name }}・{{ family.familyRelation }}）

  // error messages
  .register-row(v-show="errorMessage")
    .register-group
      .register-value
        .error-message {{ errorMessage }}

  .box-footer.register-footer
    .buttons.m-0
      button.btn.update-btn(@click="onUpdate(families)" type="button") 更新
  
  RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";

import ResidentsService, { ResidentGetModel } from "@/commons/services/Residents";
import FamiliesService, { FamilyGetModel, FamilyPermittedFunctions } from "@/commons/services/Families";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import SystemError from "@/commons/models/SystemError";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import { SenderType } from "@/commons/models/Resident";
import HelpIcon from "@/pc/basics/HelpIcon.vue";

/**
 * ### 家族の使用機能可否 設定タブコンポーネント
 *
 * - 渡された引数の使用機能権限の情報を管理する
 */
@Component({
  components: {
    RelativeLoading,
    HelpIcon
  }
})
export default class FamilyPermittedFunctionEditTab extends Vue {
  /** 利用者サービス */
  @Inject("ResidentsService") residentsService!: ResidentsService;
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "読込み中...";

  /** 編集対象の家族 */
  families: Array<FamilyListItem> = [];
  /** フィルタリング用 利用者グループリスト */
  unuseResidentGroupOptions: Array<{ value: number; label: string }> = [];
  /** フィルタリング用 利用者グループリスト */
  useResidentGroupOptions: Array<{ value: number; label: string }> = [];
  /** 利用者検索ワード */
  unuseResidentSearchWord: string = "";
  /** 利用者検索ワード */
  useResidentSearchWord: string = "";

  /** 使用不可リスト フィルタリング選択 利用者グループID */
  unuseFilteredResidentGroupId: number = 0;
  /** 使用可リスト フィルタリング選択 利用者グループID */
  useFilteredResidentGroupId: number = 0;

  /** エラーメッセージ */
  @Prop({ required: true, type: String })
  errorMessage!: string;
  /** 設定項目名 */
  @Prop({ required: true })
  type!: keyof FamilyPermittedFunctions;

  /**
   * ### 利用者グループでフィルタリングした家族を返す(使用不可リスト)
   */
  get _filteredUnuseFamilies(): Array<FamilyListItem> {
    const filterdFamiles: Array<FamilyListItem> = this.filterdFamilies(this.families, false, this.unuseFilteredResidentGroupId, this.unuseResidentSearchWord);
    return filterdFamiles;
  }

  /**
   * ### 利用者グループでフィルタリングした家族を返す(使用可リスト)
   */
  get _filteredUseFamilies(): Array<FamilyListItem> {
    const filterdFamiles: Array<FamilyListItem> = this.filterdFamilies(this.families, true, this.useFilteredResidentGroupId, this.useResidentSearchWord);
    return filterdFamiles;
  }

  /**
   * ### 家族フィルタリング
   */
  public filterdFamilies(families: Array<FamilyListItem>, used: boolean, residentGroupId: number, searchWord: string): Array<FamilyListItem> {
    let filteredFamilies: Array<FamilyListItem> = [];
    for (const family of families) {
      const key: keyof FamilyPermittedFunctions = this.type;

      // 家族の機能使用可否(Propsで渡された項目名のもの)が引数の使用可否と同じもの
      if (family.updatedPermittedFunctions[key] == used) {
        // 利用者グループ選択肢が該当する、または「全て」が選択されている
        if (residentGroupId == 0 || family.groupId == residentGroupId) {
          // 検索ワードが空または該当するものがある
          if (!searchWord || this.wordFilteringFamily(family, searchWord)) {
            filteredFamilies.push(family);
          }
        }
      }
    }
    return filteredFamilies;
  }

  public wordFilteringFamily(family: FamilyListItem, searchWord: string): boolean {
    const words: string[] = this.$filterUtil.createSearchWord(searchWord);
    for (const word of words) {
      // 利用者名
      if (-1 < family.name.indexOf(word)) {
        return true;
      }
      // 利用者名（ふりがな）
      if (family.kanaName && -1 < family.kanaName.indexOf(word)) {
        return true;
      }
      // 部屋名
      if (-1 < family.room.indexOf(word)) {
        return true;
      }
      // 家族名
      if (-1 < family.familyName.indexOf(word)) {
        return true;
      }
      // 家族役割
      if (family.familyPosition && -1 < family.familyPosition.indexOf(word)) {
        return true;
      }
    }
    return false;
  }

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.createView();
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - フィルタリングで使用するセレクトボックス用項目を作成する
   * - リスト表示対象(権限編集対象)の施設にひもづく全家族を取得する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    // グループIDとグループ名の変換用にグループ情報のMAPを作る
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    this.unuseResidentGroupOptions = this.createFilterOptions(groups);
    this.useResidentGroupOptions = this.createFilterOptions(groups);

    // 家族情報の利用者グループIDは値不定のため、利用者の情報を使用するために利用者情報を取得してMAP可する
    const residents: Array<ResidentGetModel> = await this.getResidents();
    const residentMap: Map<number, ResidentGetModel> = this.createResidentMapById(residents);

    // リスト表示するための家族を取得する
    const families: Array<FamilyListItem> = await this.getFamilies();
    const exFamilies: Array<FamilyListItem> = this.addGroupIdToFamilies(families, residentMap);
    const filterdFamilies: Array<FamilyListItem> = this.filteringLineUserFamilies(exFamilies);
    this.families = filterdFamilies;

    this.isBusy = false;

    return;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: number; label: string }> {
    let filterOptions: Array<{ value: number; label: string }> = [];

    filterOptions.push({
      value: 0,
      label: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: group.id,
          label: group.name
        });
      }
    }

    return filterOptions;
  }

  /**
   * ### 全ての利用者を取得する
   */
  public async getResidents(): Promise<Array<ResidentGetModel>> {
    let response: Array<ResidentGetModel> = [];

    try {
      response = await this.residentsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.residentsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: { startIndex: number; endIndex: number } = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.residentsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 利用者のMAPを作成する
   */
  public createResidentMapById(residents: Array<ResidentGetModel>): Map<number, ResidentGetModel> {
    const map: Map<number, ResidentGetModel> = new Map<number, ResidentGetModel>();
    for (const resident of residents) {
      map.set(resident.id, resident);
    }
    return map;
  }

  /**
   * ### 施設にひもづく全家族を取得する
   *
   * - 変更前と変更後の機能使用可否が分かるように、プロパティを追加したクラスで管理する
   */
  public async getFamilies(): Promise<Array<FamilyListItem>> {
    let response: Array<FamilyGetModel> = [];

    try {
      response = await this.familiesService.getsInFacility(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.familiesService.getCountInFacility(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.familiesService.getsInFacility(userStore, params));
        }
      }
    }

    let families: Array<FamilyListItem> = [];
    for (const family of response) {
      families.push({
        ...family,
        // ただの値格納だと参照渡しになるので、ディープコピーする
        updatedPermittedFunctions: Object.assign({}, family.familyPermittedFunctions),
        isTarget: false
      });
    }

    return families;
  }

  public addGroupIdToFamilies(families: Array<FamilyListItem>, residentMap: Map<number, ResidentGetModel>): Array<FamilyListItem> {
    const exFamilies: Array<FamilyListItem> = [];
    for (let family of families) {
      const resident: ResidentGetModel | undefined = residentMap.get(family.id); // 家族の利用者グループIDは値不定なので利用者の情報を使用する
      family.groupId = resident ? resident.groupId : family.groupId;
      exFamilies.push(family);
    }
    return exFamilies;
  }

  /**
   * ### 家族をLINEユーザーのみにフィルタリングする
   */
  public filteringLineUserFamilies(families: Array<FamilyListItem>): Array<FamilyListItem> {
    let filterdFamilies: Array<FamilyListItem> = [];

    for (const family of families) {
      if (family.senderType == SenderType.LINE) {
        filterdFamilies.push(family);
      }
    }

    return filterdFamilies;
  }

  /**
   * ### 家族の機能使用可否を変更する
   *
   * - 使用可であれば使用不可、使用不可であれば使用可に変更する
   * - 画面の選択をはずすために変更した家族の選択中(isTarget)はfalse(未選択)にする
   */
  public changePermittedFunction(families: Array<FamilyListItem>, changeTagetUsed: boolean): void {
    for (const family of families) {
      if (family.isTarget == false) {
        continue;
      }

      const key: keyof FamilyPermittedFunctions = this.type;
      if (family.updatedPermittedFunctions[key] == changeTagetUsed) {
        family.updatedPermittedFunctions[key] = !changeTagetUsed;
        family.isTarget = false;
      }
    }

    return;
  }

  /**
   * ### 更新ボタン押下イベント
   */
  public async onUpdate(families: Array<FamilyListItem>): Promise<void> {
    let updateTargetFamilies: Array<FamilyGetModel> = [];

    for (const family of families) {
      const key: keyof FamilyPermittedFunctions = this.type;
      if (family.updatedPermittedFunctions[key] != family.familyPermittedFunctions[key]) {
        family.familyPermittedFunctions = family.updatedPermittedFunctions;
        updateTargetFamilies.push(family);
      }
    }
    this.$emit("onUpdate", updateTargetFamilies);

    return;
  }
}

interface FamilyListItem extends FamilyGetModel {
  updatedPermittedFunctions: FamilyPermittedFunctions;
  isTarget: boolean;
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#family-permitted-function-edit-tab-component {
  position: relative;
  width: 100%;

  .x-scroll {
    overflow-x: auto;
    padding: 5px;
  }

  .update-btn,
  .cancel-btn {
    width: 120px;
  }

  .message {
    margin: 5px 5px 12px 5px;
  }

  .move-buttons {
    margin: 24px;

    .move-right-btn,
    .move-left-btn {
      width: 140px;
    }
    .move-right-btn {
      margin-bottom: 12px;
    }
  }

  .list-body-searchword {
    .search-filter-input-group {
      min-width: 310px;

      .search-filter-icon {
        background-color: #fff !important;
        .search-icon {
          @include svg-icon("../../../assets/search.svg", $font-base-color, 20px, 20px);
        }
      }
      .search-filter-input {
        border-left: 0;
      }
    }
  }

  .family-list {
    min-width: 300px;
    width: calc(50% - 105px);
    // 縦サイズは計算して求め、出来るだけ縦サイズを大きくする
    height: calc(100vh - 334px);
    margin: 5px;

    // デフォルトフォントサイズを指定する
    font-size: 16px;
    // 外枠はここで描画させる
    border: solid 1px $border-darkgray;

    .list-title {
      padding: 5px;
      min-height: 32px;
      font-weight: bold;
      text-align: center;
      background-color: $bg-header;
      border-bottom: solid 1px $border-lightgray;
      min-height: 35px;
    }
    .list-body {
      height: calc(100vh - 300px);
      overflow-y: scroll;
      overflow-x: auto;

      .list-body-title,
      .list-body-searchword {
        height: 48px;
        background-color: $bg-header;

        .group-select-title {
          margin-right: 16px;
        }

        .group-select-box {
          width: calc(100% - 130px);
          height: 34px;
          padding: 0 12px;
          border: 1px solid $form-border-color;
          border-radius: 0.25rem;
          &:focus {
            // chromeが自動的に付与するスタイルを無効化する
            outline: none;
            box-shadow: 0 0 0 0.2rem $form-boxshadow-select-color;
            border: 1px solid $form-border-select-color !important;
            border-radius: 0.25rem;
          }
        }
      }

      .list-body-title {
        padding: 8px;
      }

      .list-body-searchword,
      .list-body-line {
        padding: 8px;
        border-right: solid 1px $border-lightgray;
        border-bottom: solid 1px $border-lightgray;
      }
      .family-name {
        word-break: break-all;
      }
    }
  }

  .box-footer {
    padding: 10px 0 0 0 !important;
  }
}
</style>
